// Client-driven AHP `createSession` handshake plus the MC-resealed token provider,
// built on the shared protected-resource auth plumbing.

import {AhpClient, RpcError} from '@microsoft/agent-host-protocol/client'
import type {ModelSelection, SessionActiveClient, URI} from '@microsoft/agent-host-protocol'
import {
  AUTH_REQUIRED_CODE,
  type AwpsAuthenticationContext,
  authRequiredResources,
  defaultResourceTokenProvider,
  isGitHubResource,
  satisfyResources,
  type ResourceTokenProvider,
} from '../protected-resource-auth'

const MAX_AUTH_RETRIES = 1

/** localStorage key holding the working directory passed to `createSession`. */
export const WORKING_DIRECTORY_STORAGE_KEY = 'ahp_working_directory'

/** Parameters accepted by the AHP `createSession` request. */
export interface CreateSessionParams {
  channel: URI
  workingDirectory?: URI
  model?: ModelSelection
  /** Agent/workspace config (e.g. `{ target: 'worktree', baseBranch }`) binding the session to a fresh worktree. */
  config?: Record<string, unknown>
  /**
   * Eagerly claim an active-client role for the new session, registering the
   * client's tools in the agent's create-time tool set. The `clientId` MUST
   * match the one passed to `initialize`.
   */
  activeClient?: SessionActiveClient
}

// Web clients hold no raw token; MC reseals it to the host key on /connect. This
// forwards the resealed bearer to resources `isSealableResource` accepts (the
// GitHub host allowlist by default) and falls back to the plaintext provider.
export function sealedGitHubTokenProvider(
  sealedToken: string | undefined,
  fallback: ResourceTokenProvider = defaultResourceTokenProvider,
  isSealableResource: (resource: string) => boolean = isGitHubResource,
): ResourceTokenProvider {
  return resource => {
    if (sealedToken && isSealableResource(resource)) return sealedToken
    return fallback(resource)
  }
}

/** Default working directory, read from localStorage. */
export function defaultWorkingDirectory(): string | undefined {
  try {
    return globalThis.localStorage?.getItem(WORKING_DIRECTORY_STORAGE_KEY) ?? undefined
  } catch {
    return undefined
  }
}

/** Accept a bare filesystem path or a URI; the daemon parses `workingDirectory` as a URI. */
export function toWorkingDirectoryUri(value: string): URI {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return value
  return `file://${value.startsWith('/') ? '' : '/'}${value}`
}

/** Create the session, pushing tokens and retrying once if the host requires authentication. */
export async function createSessionWithAuth(
  client: AhpClient,
  params: CreateSessionParams,
  provider: ResourceTokenProvider = defaultResourceTokenProvider,
  awps?: AwpsAuthenticationContext,
): Promise<void> {
  let attempt = 0
  for (;;) {
    try {
      await client.request('createSession', params)
      return
    } catch (err) {
      if (!(err instanceof RpcError) || err.code !== AUTH_REQUIRED_CODE || attempt >= MAX_AUTH_RETRIES) {
        throw err
      }
      const satisfied = await satisfyResources(client, authRequiredResources(err.data), provider, awps)
      if (!satisfied) throw err
      attempt++
    }
  }
}
